import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { Text } from "@/components/podkit/typography/Text";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/Chart";
import { Bar, BarChart, CartesianGrid, ReferenceLine, XAxis, YAxis } from "recharts";

import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Combobox } from "@/components/flexkit/combobox/Combobox";
import {
    ceilCredits,
    formatCredits,
    rankSlotKey,
    DEFAULT_TOP_N,
    type DailyChartPoint,
    type Grouping,
    type UsageTypeFilter,
} from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

/** Sort tooltip items to match the visual stack order: highest-ranked user first, Others last. */
const makeSortTooltipPayload = (
    dataKeys: string[],
): NonNullable<React.ComponentProps<typeof ChartTooltipContent>["sortPayload"]> => {
    // dataKeys is ordered bottom→top (Others first, highest-ranked user last).
    // Reverse so the tooltip reads top→bottom matching the visual stack.
    const order = new Map(dataKeys.map((k, i) => [k, i]));
    return (payload) =>
        [...payload].sort((a, b) => {
            const ai = order.get(String(a.dataKey)) ?? 999;
            const bi = order.get(String(b.dataKey)) ?? 999;
            return bi - ai; // higher index = higher in stack = first in tooltip
        });
};

/** Render X-axis day numbers, staggering into two rows on narrow viewports. */
const StaggeredTick: FC<{
    x?: number;
    y?: number;
    index?: number;
    payload?: { value?: string };
    stagger?: boolean;
    todayIndex?: number;
}> = ({ x = 0, y = 0, index = 0, payload, stagger = false, todayIndex }) => {
    const day = String(payload?.value ?? "").replace(/\D+/g, "");
    const dy = stagger && index % 2 !== 0 ? 24 : 12;
    const isToday = todayIndex != null && index === todayIndex;
    return (
        <g>
            <text x={x} y={y} dy={dy} textAnchor="middle" className="fill-content-secondary text-xs">
                {day}
            </text>
            {isToday && !stagger && (
                <text x={x} y={y} dy={dy + 14} textAnchor="middle" className="fill-content-tertiary text-[10px]">
                    Today
                </text>
            )}
        </g>
    );
};

const cssVar = (name: string) => `rgb(var(--usage-${name}))`;

const USER_COLORS = [cssVar("1"), cssVar("3"), cssVar("5"), cssVar("7"), cssVar("9")];
const OTHERS_COLOR = "rgb(var(--surface-primary-inverted) / 0.25)";

/**
 * Map a userId-based activeLegendKey to the rank slot key if the hovered user
 * occupies this slot on this data point. Returns the slot key on match (so
 * DiagonalBar highlights it), or the original activeLegendKey (so it dims).
 */
function resolveActiveLegend(
    activeLegendKey: string | undefined,
    slotKey: string,
    point: DailyChartPoint | undefined,
): string | undefined {
    if (!activeLegendKey || !point?.slotUsers) return activeLegendKey;
    const slotUser = point.slotUsers[slotKey];
    if (slotUser && slotUser.userId === activeLegendKey) return slotKey;
    return activeLegendKey;
}
const BRAND_COLOR = cssVar("brand");
const ENVIRONMENT_COLOR = cssVar("environment");

// Member-type overlay: stack segments bottom→top within a single bar
const OVERLAY_KEYS = ["member_environment", "rest_environment", "member_agentic", "rest_agentic"] as const;
const OVERLAY_COLORS = [ENVIRONMENT_COLOR, ENVIRONMENT_COLOR, BRAND_COLOR, BRAND_COLOR];
const DIMMED_KEYS = new Set<string>(["rest_environment", "rest_agentic"]);
// Environment segments get diagonal stripes so they're distinguishable from Intelligence at a glance
const STRIPED_KEYS = new Set<string>(["environment", "member_environment", "rest_environment"]);

// Map overlay keys to their usage-type group so legend hover can dim the opposite type
const OVERLAY_TYPE_GROUP: Record<string, string> = {
    member_environment: "environment",
    rest_environment: "environment",
    member_agentic: "agentic",
    rest_agentic: "agentic",
};


/** Build an SVG path for a rectangle, optionally rounding the top-left and top-right corners. */
const roundedTopRect = (x: number, y: number, w: number, h: number, r: number): string => {
    // Clamp radius so it never exceeds half the width or the full height
    const cr = Math.min(r, w / 2, h);
    return [
        `M${x},${y + h}`,
        `V${y + cr}`,
        `Q${x},${y} ${x + cr},${y}`,
        `H${x + w - cr}`,
        `Q${x + w},${y} ${x + w},${y + cr}`,
        `V${y + h}`,
        `Z`,
    ].join(" ");
};

/** Build an SVG path for a rectangle, rounding the top-right and bottom-right corners (for horizontal bars). */
const roundedRightRect = (x: number, y: number, w: number, h: number, r: number): string => {
    const cr = Math.min(r, h / 2, w);
    return [
        `M${x},${y}`,
        `H${x + w - cr}`,
        `Q${x + w},${y} ${x + w},${y + cr}`,
        `V${y + h - cr}`,
        `Q${x + w},${y + h} ${x + w - cr},${y + h}`,
        `H${x}`,
        `Z`,
    ].join(" ");
};

/** Renders a bar segment with optional rounded edge, diagonal stripes, desaturation, and opacity transitions. */
const DiagonalBar: FC<{
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    fill?: string;
    index?: number;
    lastIndex: number;
    dataKey?: string;
    activeLegendKey?: string;
    showDivider?: boolean;
    isTopSegment?: boolean;
    horizontal?: boolean;
    fixedOpacity?: number;
    striped?: boolean;
    desaturated?: boolean;
}> = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    fill = "#ccc",
    index,
    lastIndex,
    dataKey,
    activeLegendKey,
    showDivider,
    isTopSegment,
    horizontal,
    fixedOpacity,
    striped,
    desaturated,
}) => {
    if (height <= 0 || width <= 0) return null;
    const isToday = index === lastIndex;
    const dimmed = activeLegendKey != null && dataKey !== activeLegendKey;
    const opacity = fixedOpacity ?? (dimmed ? 0.5 : 1);
    const patternId = `stripe-${fill.replace(/[^a-zA-Z0-9]/g, "")}`;

    const transition = "opacity 150ms ease-in-out, filter 150ms ease-in-out";
    const filter = desaturated ? "saturate(0)" : undefined;
    const divider = showDivider ? (
        <line x1={x} y1={y} x2={x + width} y2={y} stroke="rgb(var(--surface-primary))" strokeWidth={1} opacity={0.5} />
    ) : null;

    const radius = isTopSegment ? 4 : 0;
    const roundedPath = horizontal ? roundedRightRect : roundedTopRect;
    const shape =
        radius > 0 ? (
            <path d={roundedPath(x, y, width, height, radius)} fill={fill} />
        ) : (
            <rect x={x} y={y} width={width} height={height} fill={fill} />
        );

    const showStripes = isToday || striped;

    if (!showStripes) {
        return (
            <g opacity={opacity} style={{ transition, filter }}>
                {shape}
                {divider}
            </g>
        );
    }

    return (
        <g opacity={opacity} style={{ transition, filter }}>
            <defs>
                <pattern
                    id={patternId}
                    width={6}
                    height={6}
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                >
                    <line x1={0} y1={0} x2={0} y2={6} stroke="white" strokeWidth={2.5} opacity={0.35} />
                </pattern>
            </defs>
            {shape}
            {radius > 0 ? (
                <path d={roundedPath(x, y, width, height, radius)} fill={`url(#${patternId})`} />
            ) : (
                <rect x={x} y={y} width={width} height={height} fill={`url(#${patternId})`} />
            )}
            {divider}
        </g>
    );
};

type Props = {
    chartData: DailyChartPoint[];
    grouping: Grouping;
    onGroupingChange: (g: Grouping) => void;
    selectedType: UsageTypeFilter;
    allUsers: { userId: string; displayName: string }[];
    selectedMember?: string;
    onMemberChange: (memberId: string | undefined) => void;
    loading: boolean;
    monthLabel: string;
    errorMessage?: string;
    /** When false the "Teams" grouping tab is hidden (preaggregation flag off). */
    showTeamsGrouping?: boolean;
};

const GROUPING_OPTIONS = [
    { value: "type" as const, label: "By Type" },
    { value: "users" as const, label: "Top Users" },
    { value: "teams" as const, label: "Teams" },
];

export const EnterpriseUsageDailyChart: FC<Props> = ({
    chartData,
    grouping,
    onGroupingChange,
    selectedType,
    allUsers,
    selectedMember,
    onMemberChange,
    loading,
    monthLabel,
    errorMessage,
    showTeamsGrouping = true,
}) => {
    const [activeLegendKey, setActiveLegendKey] = useState<string | undefined>();
    const [, setActiveDataKey] = useState<string | undefined>();
    const activeDataKeyRef = useRef<string | undefined>();
    const updateActiveDataKey = useCallback((key: string | undefined) => {
        activeDataKeyRef.current = key;
        setActiveDataKey(key);
    }, []);
    const handleBarMouseLeave = useCallback(() => updateActiveDataKey(undefined), [updateActiveDataKey]);

    // Hide "By Type" tab when a specific type is already filtered — it would show the same single-bar view as "Top Users".
    // Hide "Teams" tab when preaggregation is not available.
    const visibleOptions = useMemo(
        () =>
            GROUPING_OPTIONS.filter((o) => {
                if (o.value === "type" && selectedType !== "all") return false;
                if (o.value === "teams" && !showTeamsGrouping) return false;
                return true;
            }),
        [selectedType, showTeamsGrouping],
    );

    // Reset to "users" if the active grouping tab is no longer visible
    useEffect(() => {
        if (!visibleOptions.some((o) => o.value === grouping)) {
            onGroupingChange("users");
        }
    }, [visibleOptions, grouping, onGroupingChange]);

    const { isMobileViewport } = useIsMobileViewport();
    const { isMobileViewport: isNarrowSettingsView } = useIsMobileViewport("(max-width: 1000px)");
    const { isMobileViewport: isPortrait } = useIsMobileViewport("(max-width: 767px) and (orientation: portrait)");

    const selectedMemberName = useMemo(
        () => allUsers.find((u) => u.userId === selectedMember)?.displayName,
        [allUsers, selectedMember],
    );

    const memberItems = useMemo(() => [{ userId: "all", displayName: "All" }, ...allUsers], [allUsers]);

    // Whether we're in "member highlight on By Type" mode
    const isMemberTypeOverlay = !!(selectedMember && grouping === "type");

    // Rank-slot based keys: rank_0 (highest credits) … rank_N-1 (lowest).
    const chartConfig: ChartConfig = useMemo(() => {
        if (selectedMember && selectedMemberName && grouping === "users") {
            return {
                [selectedMember]: { label: selectedMemberName, color: BRAND_COLOR },
                others: { label: "Others", color: OTHERS_COLOR },
            };
        }
        if (isMemberTypeOverlay) {
            return {
                member_environment: { label: "Environment", color: ENVIRONMENT_COLOR },
                member_agentic: { label: "Intelligence", color: BRAND_COLOR },
            };
        }
        if (grouping === "type") {
            return {
                environment: { label: "Environment", color: ENVIRONMENT_COLOR },
                agentic: { label: "Intelligence", color: BRAND_COLOR },
            };
        }
        // Both "users" and "teams" use rank-slot approach
        const cfg: ChartConfig = {};
        for (let i = 0; i < DEFAULT_TOP_N; i++) {
            cfg[rankSlotKey(i)] = {
                label: grouping === "teams" ? `Team ${i + 1}` : `Top ${i + 1}`,
                color: USER_COLORS[i % USER_COLORS.length],
            };
        }
        cfg.others = { label: grouping === "teams" ? "Others / Unassigned" : "Others", color: OTHERS_COLOR };
        return cfg;
    }, [grouping, selectedMember, selectedMemberName, isMemberTypeOverlay]);

    const dataKeys = useMemo(() => {
        if (selectedMember && grouping === "users") return ["others", selectedMember];
        if (isMemberTypeOverlay) return [...OVERLAY_KEYS];
        if (grouping === "type") return ["environment", "agentic"];
        const slots = Array.from({ length: DEFAULT_TOP_N }, (_, i) => rankSlotKey(i));
        return ["others", ...slots.reverse()];
    }, [grouping, selectedMember, isMemberTypeOverlay]);

    const colors = useMemo(() => {
        if (selectedMember && grouping === "users") return [OTHERS_COLOR, BRAND_COLOR];
        if (isMemberTypeOverlay) return [...OVERLAY_COLORS];
        if (grouping === "type") return [ENVIRONMENT_COLOR, BRAND_COLOR];
        const slotColors = Array.from({ length: DEFAULT_TOP_N }, (_, i) => USER_COLORS[i % USER_COLORS.length]);
        return [OTHERS_COLOR, ...slotColors.reverse()];
    }, [grouping, selectedMember, isMemberTypeOverlay]);

    const sortTooltipPayload = useMemo(() => {
        const sorter = makeSortTooltipPayload(dataKeys);
        const isRankSlotGrouping = grouping === "users" || grouping === "teams";
        return (payload: Parameters<typeof sorter>[0]) => {
            let filtered = payload;
            if (isMemberTypeOverlay) {
                // Hide the dimmed rest_* segments from the tooltip
                filtered = filtered.filter((item) => !DIMMED_KEYS.has(String(item.dataKey)));
            }
            if (isRankSlotGrouping) {
                // Hide rank slots with no data so fallback labels like "Team 1" don't appear.
                // Recharts passes undefined or 0 for slots without data on a given day.
                filtered = filtered.filter(
                    (item) => item.value != null && Number(item.value) > 0,
                );
            }
            return sorter(filtered);
        };
    }, [dataKeys, isMemberTypeOverlay, grouping]);

    // Resolve rank slot keys (rank_0 …) to actual user/team display names for tooltips.
    const resolveSlotName = useCallback(
        (dataKey: string, payload: Record<string, unknown>): string | undefined => {
            if (isMemberTypeOverlay) {
                if (dataKey === "member_environment") return "Environment";
                if (dataKey === "member_agentic") return "Intelligence";
            }
            // Resolve from the slot map matching the active grouping to avoid
            // accidental cross-contamination between team and user names.
            const slotTeams = payload.slotTeams as Record<string, { displayName: string }> | undefined;
            if (slotTeams) return slotTeams[dataKey]?.displayName;
            const slotUsers = payload.slotUsers as Record<string, { displayName: string }> | undefined;
            return slotUsers?.[dataKey]?.displayName;
        },
        [isMemberTypeOverlay],
    );

    // The default Top Users view hides the legend entirely, so chartConfig suffices.
    const legendConfig = chartConfig;

    // Only show diagonal stripes on the last bar if it represents today
    const isCurrentMonth = useMemo(() => {
        if (!chartData.length) return false;
        const todayLabel = new Date().toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            timeZone: "UTC",
        });
        return chartData[chartData.length - 1]?.date === todayLabel;
    }, [chartData]);

    // Compute 4 evenly spaced Y-axis ticks that sit tightly above the data max.
    // Rounds the ceiling to the nearest multiple of a clean number, then divides into 4 equal steps.
    const yAxisTicks = useMemo(() => {
        if (!chartData.length) return [0];
        const maxVal = chartData.reduce((max, point) => {
            const sum = dataKeys.reduce((s, k) => s + (Number(point[k]) || 0), 0);
            return Math.max(max, sum);
        }, 0);
        if (maxVal === 0) return [0];

        const intervals = 4;
        // Pick a rounding unit: the smallest power-of-10 that keeps the ceiling within ~10% of max
        const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)) - 1);
        const unit = magnitude * intervals; // must be divisible by 4
        const ceiling = Math.ceil(maxVal / unit) * unit;
        const step = ceiling / intervals;

        return Array.from({ length: intervals + 1 }, (_, i) => Math.round(i * step));
    }, [chartData, dataKeys]);

    const tooltipLabelFormatter = useCallback(
        (label: string) => {
            const lastDate = chartData[chartData.length - 1]?.date;
            const base = label === lastDate ? `Today \u00b7 ${label}` : String(label);
            if (isMemberTypeOverlay && selectedMemberName) {
                return `${base} \u00b7 ${selectedMemberName}`;
            }
            return base;
        },
        [chartData, isMemberTypeOverlay, selectedMemberName],
    );

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                <Text className="whitespace-nowrap text-lg font-semibold text-content-primary">
                    {new Date(Date.parse(`1 ${monthLabel}`)).toLocaleDateString(undefined, { month: "long" })} daily
                    usage
                </Text>
                <div className="flex flex-wrap items-center gap-2">
                    {(grouping === "users" || grouping === "type") && (
                        <Combobox
                            value={selectedMember ?? "all"}
                            onValueChange={(v) => onMemberChange(v === "all" ? undefined : v)}
                            filterPlaceholder="Search members"
                            className="max-md:flex-1 h-[33px] w-[140px] order-2 md:order-1"
                        >
                            <Combobox.Value>
                                <Combobox.ValueLabel>{selectedMemberName ?? "All"}</Combobox.ValueLabel>
                            </Combobox.Value>
                            <Combobox.List
                                items={memberItems}
                                searchKeys={["displayName"]}
                                noMatchesComponent={<Combobox.Empty>No members found</Combobox.Empty>}
                            >
                                {(item) => (
                                    <Combobox.ListItem key={item.userId} value={item.userId} title={item.displayName} />
                                )}
                            </Combobox.List>
                        </Combobox>
                    )}
                    {/* Override @container (sets container-type:inline-size, collapsing width in flex parents)
                       and force flex-row since we always want horizontal layout here. */}
                    <Tabs
                        value={grouping}
                        onValueChange={(v) => onGroupingChange(v as Grouping)}
                        className="w-fit [container-type:normal] order-1 md:order-2"
                    >
                        <TabsList className="flex-row">
                            {visibleOptions.map((opt) => (
                                <TabsTrigger
                                    key={opt.value}
                                    value={opt.value}
                                    className="h-7 flex-grow-0 px-3 py-1 text-sm"
                                >
                                    {opt.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {loading ? (
                <div className="flex h-[320px] items-center justify-center gap-2" role="status" aria-live="polite">
                    <IconSpinner className="animate-spin" size="base" aria-hidden={true} />
                    <Text>Loading chart data...</Text>
                </div>
            ) : errorMessage || chartData.length === 0 ? (
                <EmptyChartOverlay message={errorMessage ?? `No credit usage for ${monthLabel}`} />
            ) : (
                <>
                    {isPortrait ? (
                        <ChartContainer
                            config={chartConfig}
                            className="w-full"
                            style={{ height: Math.max(320, chartData.length * 28 + 40) }}
                        >
                            <BarChart
                                data={chartData}
                                layout="vertical"
                                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                barCategoryGap="10%"
                            >
                                <CartesianGrid
                                    horizontal={false}
                                    strokeDasharray="3 3"
                                    stroke="rgb(var(--border-base) / var(--border-base-baseline-opacity))"
                                />
                                <YAxis
                                    dataKey="date"
                                    type="category"
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    width={28}
                                    tickMargin={4}
                                    tick={(props: {
                                        x: number;
                                        y: number;
                                        index: number;
                                        payload: { value: string };
                                    }) => {
                                        const { y, index, payload } = props;
                                        const day = String(payload.value).replace(/\D+/g, "");
                                        const isToday = isCurrentMonth && index === chartData.length - 1;
                                        return (
                                            <text
                                                x={14}
                                                y={y}
                                                dy={4}
                                                textAnchor="middle"
                                                className={`text-xs ${isToday ? "fill-content-primary font-semibold" : "fill-content-secondary"}`}
                                            >
                                                {isToday ? `${day}·` : day}
                                            </text>
                                        );
                                    }}
                                />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v: number) => formatCredits(v)}
                                    ticks={yAxisTicks}
                                    domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                                    height={24}
                                />
                                <ChartTooltip
                                    cursor={false}
                                    content={(props) => (
                                        <ChartTooltipContent
                                            {...props}
                                            className="w-[280px]"
                                            labelFormatter={tooltipLabelFormatter}
                                            valueFormatter={(v) => Math.round(ceilCredits(Number(v))).toLocaleString()}
                                            nameFormatter={resolveSlotName}
                                            sortPayload={sortTooltipPayload}
                                            showTotal={!isMemberTypeOverlay}
                                            activeDataKey={activeDataKeyRef.current}
                                        />
                                    )}
                                />
                                {dataKeys.map((key, i) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        name={(chartConfig[key]?.label as string) ?? key}
                                        stackId="stack"
                                        fill={colors[i]}
                                        maxBarSize={24}
                                        isAnimationActive={false}
                                        onMouseOver={() => updateActiveDataKey(key)}
                                        onMouseOut={handleBarMouseLeave}
                                        shape={(props: object) => {
                                            const p = props as Record<string, unknown>;
                                            const pointIndex = p.index as number | undefined;
                                            const point = pointIndex != null ? chartData[pointIndex] : undefined;
                                            const isTop =
                                                point != null &&
                                                key ===
                                                    [...dataKeys].reverse().find((k) => (Number(point[k]) || 0) > 0);
                                            const isDimmed = DIMMED_KEYS.has(key);
                                            const isStriped = STRIPED_KEYS.has(key);
                                            // Resolve userId-based activeLegendKey to this slot's
                                            // key so DiagonalBar can match it against dataKey.
                                            const resolved = resolveActiveLegend(activeLegendKey, key, point);
                                            // In overlay mode, desaturate segments of the opposite usage type
                                            const activeGroup = activeLegendKey ? OVERLAY_TYPE_GROUP[activeLegendKey] : undefined;
                                            const keyGroup = OVERLAY_TYPE_GROUP[key];
                                            const isInactiveType = !!(activeGroup && keyGroup && activeGroup !== keyGroup);
                                            return (
                                                <DiagonalBar
                                                    {...p}
                                                    lastIndex={isCurrentMonth ? chartData.length - 1 : -1}
                                                    dataKey={key}
                                                    activeLegendKey={isInactiveType ? undefined : resolved}
                                                    showDivider={false}
                                                    isTopSegment={isTop}
                                                    horizontal
                                                    fixedOpacity={isDimmed ? 0.25 : undefined}
                                                    striped={isStriped}
                                                    desaturated={isInactiveType}
                                                />
                                            );
                                        }}
                                    />
                                ))}
                                <ReferenceLine
                                    x={0}
                                    stroke="rgb(var(--content-tertiary))"
                                    strokeWidth={1}
                                    ifOverflow="extendDomain"
                                />
                            </BarChart>
                        </ChartContainer>
                    ) : (
                        <ChartContainer config={chartConfig} className="h-[320px] w-full">
                            <BarChart
                                data={chartData}
                                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                barCategoryGap="20%"
                            >
                                <CartesianGrid
                                    vertical={false}
                                    strokeDasharray="3 3"
                                    stroke="rgb(var(--border-base) / var(--border-base-baseline-opacity))"
                                />
                                <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    tick={
                                        <StaggeredTick
                                            stagger={isNarrowSettingsView}
                                            todayIndex={isCurrentMonth ? chartData.length - 1 : undefined}
                                        />
                                    }
                                    height={
                                        isCurrentMonth
                                            ? isNarrowSettingsView
                                                ? 52
                                                : 38
                                            : isNarrowSettingsView
                                              ? 40
                                              : 24
                                    }
                                />
                                {!isMobileViewport && (
                                    <YAxis
                                        tickLine={false}
                                        axisLine={false}
                                        tickMargin={8}
                                        tickFormatter={(v: number) => formatCredits(v)}
                                        ticks={yAxisTicks}
                                        domain={[0, yAxisTicks[yAxisTicks.length - 1]]}
                                    />
                                )}
                                <ChartTooltip
                                    cursor={false}
                                    content={(props) => (
                                        <ChartTooltipContent
                                            {...props}
                                            className="w-[340px]"
                                            labelFormatter={tooltipLabelFormatter}
                                            valueFormatter={(v) => Math.round(ceilCredits(Number(v))).toLocaleString()}
                                            nameFormatter={resolveSlotName}
                                            sortPayload={sortTooltipPayload}
                                            showTotal={!isMemberTypeOverlay}
                                            activeDataKey={activeDataKeyRef.current}
                                        />
                                    )}
                                />
                                {dataKeys.map((key, i) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        name={(chartConfig[key]?.label as string) ?? key}
                                        stackId="stack"
                                        fill={colors[i]}
                                        maxBarSize={48}
                                        isAnimationActive={false}
                                        onMouseOver={() => updateActiveDataKey(key)}
                                        onMouseOut={handleBarMouseLeave}
                                        shape={(props: object) => {
                                            const p = props as Record<string, unknown>;
                                            const pointIndex = p.index as number | undefined;
                                            const point = pointIndex != null ? chartData[pointIndex] : undefined;
                                            const isDimmed = DIMMED_KEYS.has(key);
                                            const isStriped = STRIPED_KEYS.has(key);
                                            const isTop =
                                                point != null &&
                                                key ===
                                                    [...dataKeys].reverse().find((k) => (Number(point[k]) || 0) > 0);
                                            const resolved = resolveActiveLegend(activeLegendKey, key, point);
                                            // In overlay mode, desaturate segments of the opposite usage type
                                            const activeGroup = activeLegendKey ? OVERLAY_TYPE_GROUP[activeLegendKey] : undefined;
                                            const keyGroup = OVERLAY_TYPE_GROUP[key];
                                            const isInactiveType = !!(activeGroup && keyGroup && activeGroup !== keyGroup);
                                            return (
                                                <DiagonalBar
                                                    {...p}
                                                    lastIndex={isCurrentMonth ? chartData.length - 1 : -1}
                                                    dataKey={key}
                                                    activeLegendKey={isInactiveType ? undefined : resolved}
                                                    showDivider={false}
                                                    isTopSegment={isTop}
                                                    fixedOpacity={isDimmed ? 0.25 : undefined}
                                                    striped={isStriped}
                                                    desaturated={isInactiveType}
                                                />
                                            );
                                        }}
                                    />
                                ))}
                                <ReferenceLine
                                    y={0}
                                    stroke="rgb(var(--content-tertiary))"
                                    strokeWidth={1}
                                    ifOverflow="extendDomain"
                                />
                            </BarChart>
                        </ChartContainer>
                    )}
                    {(grouping === "users" || grouping === "teams") && !selectedMember ? (
                        <Text className="pt-2 text-center text-xs text-content-tertiary">
                            {grouping === "teams"
                                ? "Hover a bar to see which teams used the most credits that day"
                                : "Hover a bar to see who used the most credits that day"}
                        </Text>
                    ) : (
                        <CustomLegend
                            config={legendConfig}
                            activeLegendKey={activeLegendKey}
                            onLegendEnter={setActiveLegendKey}
                            onLegendLeave={() => setActiveLegendKey(undefined)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const EmptyChartOverlay: FC<{ message: string }> = ({ message }) => (
    <div className="relative h-[320px]">
        <ChartContainer config={{}} className="h-full w-full opacity-40">
            <BarChart margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid
                    vertical={false}
                    strokeDasharray="3 3"
                    stroke="rgb(var(--border-base) / var(--border-base-baseline-opacity))"
                />
                <XAxis tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
            </BarChart>
        </ChartContainer>
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <Text className="text-content-secondary">{message}</Text>
        </div>
    </div>
);

/** Legend swatch — shows diagonal stripes for Environment keys to match the bar pattern. */
const LegendSwatch: FC<{ color: string; striped: boolean }> = ({ color, striped }) =>
    striped ? (
        <svg className="size-3.5 shrink-0" viewBox="0 0 14 14">
            <defs>
                <pattern
                    id="legend-stripe"
                    width={6}
                    height={6}
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                >
                    <line x1={0} y1={0} x2={0} y2={6} stroke="white" strokeWidth={2.5} opacity={0.35} />
                </pattern>
            </defs>
            <rect width={14} height={14} rx={2} fill={color} />
            <rect width={14} height={14} rx={2} fill="url(#legend-stripe)" />
        </svg>
    ) : (
        <div className="size-3.5 shrink-0 rounded-[2px]" style={{ backgroundColor: color }} />
    );

const CustomLegend: FC<{
    config: ChartConfig;
    activeLegendKey?: string;
    onLegendEnter: (key: string) => void;
    onLegendLeave: () => void;
}> = ({ config, activeLegendKey, onLegendEnter, onLegendLeave }) => (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 pt-2">
        {Object.entries(config).map(([key, entry]) => {
            const dimmed = activeLegendKey != null && activeLegendKey !== key;
            return (
                <div
                    key={key}
                    className="flex cursor-default items-center gap-1.5 whitespace-nowrap text-sm text-content-primary transition-opacity"
                    style={{ opacity: dimmed ? 0.5 : 1 }}
                    onMouseEnter={() => onLegendEnter(key)}
                    onMouseLeave={onLegendLeave}
                >
                    <LegendSwatch color={entry.color!} striped={STRIPED_KEYS.has(key)} />
                    {entry.label}
                </div>
            );
        })}
    </div>
);
